import CheckIcon from "@assets/icons/check.svg";
import VerifyAccountIcon from "@assets/pictures/verify-account.svg";
import { useI18n } from "@core/i18n/i18n";
import { useNavigate } from "@core/routing/use-navigate";
import { analyticsService } from "@core/service-container";
import { AnalyticsCategory } from "@domain/analytics/analytics-types";
import styled from "@emotion/styled";
import { Button } from "@ui/components/buttons/button";
import { ContactSupport } from "@ui/components/buttons/contact-support";
import { TertiaryButton } from "@ui/components/buttons/tertiary-button";
import { useDimensions } from "@ui/hooks/use-dimensions";
import { RoutesPath } from "@ui/navigation/routes";
import { useRegistrationCountryUserConfig } from "@ui/pages/register/steps/hooks/use-registration-country-user-config";
import { physioMeMobileBreakpoint, row, stack } from "@ui/style";
import { colors } from "@ui/style/colors";
import React from "react";

interface IntroductionStepProps {
	onNext: () => void;
}

export const IntroductionStep: React.FC<IntroductionStepProps> = ({ onNext }) => {
	const { format } = useI18n();
	const navigate = useNavigate();
	const { idTypeOptions } = useRegistrationCountryUserConfig();
	const { isMobile } = useDimensions();
	return (
		<Container>
			{isMobile && (
				<RowContactSupport>
					<SubnavigationContainer>
						<ContactSupport uiStyle="secondary" />
					</SubnavigationContainer>
				</RowContactSupport>
			)}
			<VerifyAccountIcon />
			<h3>{format("register.introduction.title")}</h3>
			<p className="p-size-m">{format("register.introduction.description")}</p>
			<Requirements>
				<p className="p-size-m">{format("register.introduction.requirements.title")}</p>
				<Row>
					<div>
						<IconContainer>
							<CheckIcon width={12} height={12} color={colors.primary} />
						</IconContainer>
					</div>
					<p className="p-size-m">
						{format("register.introduction.requirements.license", {
							licenses: idTypeOptions.map(m => m.label).join(", "),
						})}
					</p>
				</Row>
				<Row>
					<div>
						<IconContainer>
							<CheckIcon width={12} height={12} color={colors.primary} />
						</IconContainer>
					</div>
					<p className="p-size-m">{format("register.introduction.requirements.id")}</p>
				</Row>
			</Requirements>
			<NextButton label={format("global.dialog.continue")} onClick={onNext} />
			<TertiaryButton
				label={format("register.introduction.cta.back")}
				onClick={() => {
					analyticsService.trackEvent({
						category: AnalyticsCategory.REGISTRATION,
						action: "doItLaterCta",
					});
					navigate(RoutesPath.Home);
				}}
			/>
		</Container>
	);
};

const RowContactSupport = styled.span`
	${stack(8, undefined, "stretch")}
	margin-bottom: 50px;
`;

const SubnavigationContainer = styled.span`
	align-self: start;
	position: absolute;
	top: 0px;
	right: 0px;

	@media ${physioMeMobileBreakpoint} {
		margin-top: 100px;
		margin-right: 16px;
	}
`;

const Container = styled.div`
	${stack(24, "center", "center")};
	max-width: 450px;
	margin: 0 12px;
`;

const Requirements = styled.div`
	${stack(12, undefined, "flexstart")}
`;

const IconContainer = styled.div`
	width: 20px;
	height: 20px;
	border-radius: 10px;
	background-color: ${colors.primary10};
	justify-content: center;
	align-items: center;
	display: flex;
	margin-top: 2px;
`;

const Row = styled.div`
	${row(12, undefined, "flexstart")}
`;

const NextButton = styled(Button)`
	align-self: stretch;
	margin-top: 16px;
`;
